import { useMemo } from "react";

import { useLocalStorage } from "usehooks-ts";

import { DashboardFormModal } from "src/analytics/dashboard/DashboardFormModal";
import { DashboardGrid } from "src/analytics/dashboard/DashboardGrid";
import { DashboardGridSkeleton } from "src/analytics/dashboard/DashboardGridSkeleton";
import { DashboardMetricsProvider } from "src/analytics/dashboard/DashboardMetricsContext";
import { DashboardRefreshButton } from "src/analytics/dashboard/DashboardRefreshButton";
import { DashboardTabs } from "src/analytics/dashboard/DashboardTabs";
import { defaultDashboardWindow } from "src/analytics/dashboard/dashboardFilterParams";
import { JustPinnedProvider } from "src/analytics/dashboard/JustPinnedContext";
import { useDashboard } from "src/analytics/dashboard/useDashboard";
import { useListDashboardsQuery } from "src/api/analytics-dashboard/dashboard";
import { DocumentTitle } from "src/base-components/DocumentTitle";
import { Realm } from "src/clients/analytics-dashboard";
import { useModal } from "src/design-system/Modal";
import { useCapabilities } from "src/hooks/useCapabilities";
import { PageHeader } from "src/layout/PageHeader";
import { SubHeaderDatePicker } from "src/layout/SubHeader";
import { DashboardPageParams } from "src/router/urls";
import { type StrictDateRange, useTimeWindow } from "src/utils/timeWindow";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const WorkspaceInsightsContent: React.FC = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const defaultWindow = useMemo<StrictDateRange>(
    () => defaultDashboardWindow(),
    [],
  );
  const { dateRangePickerValue, onDateRangePickerChange, timeWindow } =
    useTimeWindow<StrictDateRange>(
      defaultWindow,
      "workspaceInsightsTimeWindow",
    );

  const { data: dashboards } = useListDashboardsQuery(Realm.WORKSPACE, wsId);
  const [activeDashboardId, setActiveDashboardId] = useLocalStorage<
    string | undefined
  >(`workspaceInsightsActiveDashboard:${wsId}`, undefined);
  const resolvedDashboardId =
    activeDashboardId && dashboards?.some((d) => d.id === activeDashboardId)
      ? activeDashboardId
      : dashboards?.[0]?.id;

  const { workspaceInsights } = useCapabilities();
  const { dashboard } = useDashboard(
    Realm.WORKSPACE,
    wsId,
    resolvedDashboardId,
  );
  const pins = resolvedDashboardId ? Object.values(dashboard.pins) : [];

  const {
    isOpen: isCreateOpen,
    openModal: openCreateModal,
    closeModal: closeCreateModal,
    afterLeave: createAfterLeave,
  } = useModal();

  const {
    isOpen: isEditOpen,
    data: editingDashboardId,
    openModal: openEditModal,
    closeModal: closeEditModal,
    afterLeave: editAfterLeave,
  } = useModal<string>();

  return (
    <JustPinnedProvider key={`${Realm.WORKSPACE}:${wsId}`}>
      <DashboardMetricsProvider
        // Workspace insights aggregate flow data, so they read the flow analytics context.
        dataSource="flow_analytics"
        pins={pins}
        timeWindow={timeWindow}
      >
        <div
          className="flex h-full flex-col"
          data-loc="workspace-insights-content"
        >
          <DocumentTitle segments={["Insights"]} />
          <PageHeader title="Insights" />
          <DashboardTabs
            activeDashboardId={resolvedDashboardId ?? ""}
            canCreate={workspaceInsights.canAccess}
            realm={Realm.WORKSPACE}
            realmId={wsId}
            onCreateRequest={() => openCreateModal()}
            onDashboardChange={setActiveDashboardId}
            onEditRequest={openEditModal}
          >
            <SubHeaderDatePicker
              placeholder="Last 30 days"
              value={dateRangePickerValue}
              onChange={onDateRangePickerChange}
            />
            <DashboardRefreshButton />
          </DashboardTabs>
          <div className="flex h-full flex-col overflow-auto p-5">
            {resolvedDashboardId ? (
              <DashboardGrid
                dashboardId={resolvedDashboardId}
                labelDictionaries={{}}
                realm={Realm.WORKSPACE}
                realmId={wsId}
              />
            ) : (
              <DashboardGridSkeleton />
            )}
          </div>
          <DashboardFormModal
            afterLeave={createAfterLeave}
            open={isCreateOpen}
            realm={Realm.WORKSPACE}
            realmId={wsId}
            onClose={closeCreateModal}
            onCreated={setActiveDashboardId}
          />
          <DashboardFormModal
            afterLeave={editAfterLeave}
            dashboardId={editingDashboardId}
            open={isEditOpen}
            realm={Realm.WORKSPACE}
            realmId={wsId}
            onClose={closeEditModal}
          />
        </div>
      </DashboardMetricsProvider>
    </JustPinnedProvider>
  );
};
